/** Match category from tree node (id or code; tolerate string/number mismatch). */
export const categoryMatchesId = (cat, categoryId) =>
  cat.id === categoryId ||
  cat.code === categoryId ||
  (categoryId != null &&
    (String(cat.id) === String(categoryId) || String(cat.code) === String(categoryId)));

/** Match chapter numbers from API (string) vs UI (possibly number). */
export const chapterNumbersMatch = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

/** Full HSE document number for display (includes revision suffix -vN when applicable). */
export const formatHseDocumentDisplayNumber = (doc) =>
  doc?.full_document_number || doc?.document_number || null;

export const findDisciplineById = (disciplinesList, disciplineId) => {
  if (!disciplineId) return null;
  return (
    (disciplinesList || []).find((d) => {
      const id = d.id ?? d.code;
      return (
        id === disciplineId ||
        String(id) === String(disciplineId) ||
        Number(id) === Number(disciplineId)
      );
    }) || null
  );
};

/**
 * Find a category across all loaded disciplines.
 * Prefer categories under preferredDiscipline when provided.
 */
export const findCategoryInDisciplines = (disciplinesList, categoryId, preferredDiscipline) => {
  if (!categoryId) return null;
  const pickFrom = (d) => d?.categories?.find((cat) => categoryMatchesId(cat, categoryId));
  if (preferredDiscipline) {
    const fromPreferred = pickFrom(preferredDiscipline);
    if (fromPreferred) return fromPreferred;
  }
  for (const d of disciplinesList || []) {
    const found = pickFrom(d);
    if (found) return found;
  }
  return null;
};

/**
 * Find a chapter anywhere in the nested tree returned by disciplines API.
 */
export const findChapterInCategoryTree = (category, chapterNumber) => {
  if (!category || !chapterNumber) return null;

  const walk = (chapters) => {
    if (!chapters || chapters.length === 0) return null;
    for (const ch of chapters) {
      if (chapterNumbersMatch(ch.number, chapterNumber)) return ch;
      const nested = ch.sub_chapters || ch.subChapters;
      const found = walk(nested);
      if (found) return found;
    }
    return null;
  };

  return walk(category.chapters || category.root_chapters);
};

export const resolveDocumentFilterContext = (
  disciplinesList,
  { selectedDiscipline, selectedCategory, selectedChapter }
) => {
  const discipline = findDisciplineById(disciplinesList, selectedDiscipline);
  const category = findCategoryInDisciplines(
    disciplinesList,
    selectedCategory,
    discipline
  );
  const chapter =
    selectedChapter && category
      ? findChapterInCategoryTree(category, selectedChapter)
      : null;

  const summaryParts = [];
  if (discipline?.name) summaryParts.push(discipline.name);
  if (category?.name) summaryParts.push(category.name);
  if (chapter) {
    summaryParts.push(
      chapter.title
        ? `${chapter.number} - ${chapter.title}`
        : String(chapter.number)
    );
  } else if (selectedChapter) {
    summaryParts.push(String(selectedChapter));
  }

  const hasSidebarFilter = !!(
    (selectedDiscipline && selectedDiscipline !== "") ||
    selectedCategory ||
    selectedChapter
  );

  return {
    discipline,
    category,
    chapter,
    hasSidebarFilter,
    summaryParts,
    summaryLabel: summaryParts.join(" › "),
  };
};

export const buildNewDocumentSearchParams = ({ disciplineId, categoryId, chapterNumber }) => {
  const params = new URLSearchParams();
  if (disciplineId) params.append("discipline", disciplineId);
  if (categoryId) params.append("category", categoryId);
  if (chapterNumber) params.append("chapter", chapterNumber);
  return params;
};
